/* eslint-disable jsx-a11y/img-redundant-alt */

import React, { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import cn from '@/utils/cn';
import Text from '@/components/ui/Text';
import { Image, TableRowData } from './types';
import ImageHeaderCell from './ImageHeaderCell';
import PrimaryImageToggle from './PrimaryImageToggle';
import SelectAllCheckbox from './SelectAllCheckbox';

const ROW_HEADER_LABELS = {
  altText: 'Alt Text',
  credits: 'Credits',
  dimensions: 'Dimensions',
  englishImageOnly: 'EN Only?',
  fileName: 'File Name',
  imageData: 'Image Data',
  license: 'License',
  numberOfUses: 'Number of Uses',
  size: 'Size',
  useAsPrimary: 'Use as Primary',
} as const;

const columnHelper = createColumnHelper<TableRowData>();

const useColumns = (newImage: Image, duplicateImages: Image[], dependencies: any[]) => useMemo(() => {
  const headerColumn = columnHelper.accessor('header', {
    cell: (info) => {
      if (info.getValue() === 'imageData') {
        return (
          <div className="tw-font-medium tw-text-gray-700 tw-px-3 tw-py-1 tw-text-right">
            <Text
              asChild
              className={cn(
                'tw-absolute tw-left-0 tw-top-0 tw-flex tw-items-center tw-gap-1',
                'tw-p-3 -tw-ml-1 -tw-mt-1 tw-cursor-pointer',
              )}
              size="sm"
              variant="label"
              weight="medium"
            >
              <label htmlFor="dup-image-select-all">
                <SelectAllCheckbox imageIds={[newImage.id, ...duplicateImages.map(im => im.id)]} />
                Select All
              </label>
            </Text>
            <span>{ROW_HEADER_LABELS[info.getValue()]}</span>
          </div>
        );
      }

      return (
        <div className="tw-font-medium tw-text-gray-700 tw-px-3 tw-py-1 tw-text-right">
          {ROW_HEADER_LABELS[info.getValue()]}
        </div>
      );
    },
    header: '',
    id: 'header',
  });

  const uploadedImageColumn = columnHelper.accessor('uploadedImage', {
    cell: (info) => {
      const rowData = info.row.original;
      const value = info.getValue();

      if (rowData.header === 'imageData' && value) {
        return (
          <ImageHeaderCell
            imageId={newImage.id}
            imageSrc={value}
            label="Uploaded Image"
          />
        );
      }

      if (rowData.header === 'useAsPrimary') {
        return (
          <PrimaryImageToggle imageId={newImage.id} />
        );
      }

      return (
        <div className="tw-text-left tw-px-2 tw-py-1">
          {value}
        </div>
      );
    },
    header: 'Uploaded Image',
    id: 'uploaded-image',
    meta: {
      imageId: newImage.id,
    },
  });

  const duplicateImageColumns = duplicateImages.map((duplicateImage, index) => (
    columnHelper.accessor((row: TableRowData) => row.duplicateImages[index] || '', {
      cell: (info) => {
        const rowData = info.row.original;
        const value = info.getValue();

        if (rowData.header === 'imageData' && value) {
          return (
            <ImageHeaderCell
              isDuplicate
              imageId={duplicateImage.id}
              imageSrc={value}
              label="Duplicate Image"
            />
          );
        }

        if (rowData.header === 'useAsPrimary') {
          return <PrimaryImageToggle imageId={duplicateImage.id} />;
        }

        return (
          <div className="tw-text-left tw-px-2 tw-py-1">
            {value}
          </div>
        );
      },
      header: 'Duplicate Image',
      id: `duplicate-${index}`,
      meta: {
        imageId: duplicateImage.id,
      },
    })));

  return [headerColumn, uploadedImageColumn, ...duplicateImageColumns];
}, [newImage, duplicateImages, ...dependencies]);

export default useColumns;
